import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import api, { getErrorMessage } from '@/services/api';

/**
 * #356: the insurer's Broker Requests screen. A broker asks to sell this
 * insurer's products; the insurer views the request, approves it (optionally
 * for a subset of products), rejects it with a required reason, and reads the
 * history. The dashboard's Pending Broker Requests card opens `?status=pending`.
 * Either side's decision is final (country admin override): the trail says who.
 */

export interface BrokerRequest {
  id: string;
  broker_name: string;
  broker_code: string;
  broker_status: string;
  licence_number: string;
  licence_valid_until: string | null;
  licence_state: 'valid' | 'expiring' | 'expired' | 'unknown';
  status: 'pending' | 'approved' | 'declined';
  note: string;
  decline_reason: string;
  decided_by: string;
  decided_by_side: string;
  decided_at: string | null;
  created_at: string | null;
  can_approve: boolean;
  approve_blocker: string;
  status_history?: Array<{ status: string; timestamp: string; user: string; side?: string; reason?: string }>;
  relation_status?: string;
  products?: Array<{ id: string; name: string }>;
}

const BASE = '/insurers/insurance/broker-requests/';
export const MIN_REASON = 5;

const TH = 'px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300';
const TD = 'px-4 py-2 text-sm text-gray-900 dark:text-gray-100';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', declined: 'error' } as const;
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', declined: 'Rejected' } as const;
const LICENCE_VARIANT = { valid: 'success', expiring: 'warning', expired: 'error', unknown: 'default' } as const;
const SIDE_LABEL: Record<string, string> = { insurer: 'Insurer', country_admin: 'Country admin' };

const date = (value: string | null | undefined) => (value ? value.slice(0, 10) : '');
const excerpt = (text: string) => (text.length > 60 ? `${text.slice(0, 57)}...` : text);

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        active
          ? 'border-b-2 border-primary-600 px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-400'
          : 'px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
      }
    >
      {children}
    </button>
  );
}

export function BrokerRequestsPage() {
  const [params, setParams] = useSearchParams();
  // `?status=pending` (the dashboard card) is the Pending tab; anything else is History.
  const tab: 'pending' | 'history' = (params.get('status') || 'pending').toLowerCase() === 'pending' ? 'pending' : 'history';
  const [openId, setOpenId] = useState<string | null>(null);
  const { can } = useInsurerPermissions();
  const canDecide = can('broker_requests.edit');

  const { data, isLoading, isError } = useQuery<{ results: BrokerRequest[] }>({
    queryKey: ['insurerBrokerRequests', tab],
    queryFn: async () =>
      (await api.get(BASE, { params: { status: tab === 'pending' ? 'pending' : 'decided' } })).data,
  });
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title="Broker Requests" subtitle="Brokers asking to sell your products" />
      <div role="tablist" className="flex border-b border-gray-200 dark:border-gray-700">
        <TabButton active={tab === 'pending'} onClick={() => setParams({ status: 'pending' })}>
          Pending
        </TabButton>
        <TabButton active={tab === 'history'} onClick={() => setParams({ status: 'history' })}>
          History
        </TabButton>
      </div>
      <Card className="p-4 sm:p-6">
        {isLoading ? (
          <LoadingSpinner />
        ) : isError ? (
          <p className="text-sm text-red-600 dark:text-red-400">Broker requests could not be loaded.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {tab === 'pending' ? 'No pending broker requests' : 'No decided broker requests yet'}
          </p>
        ) : (
          <>
            <table className="hidden min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:table">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className={TH}>Broker</th>
                  <th className={TH}>Code</th>
                  <th className={TH}>Licence</th>
                  <th className={TH}>Requested</th>
                  <th className={TH}>{tab === 'pending' ? 'Note' : 'Decision'}</th>
                  <th className={TH}>
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className={TD}>{r.broker_name}</td>
                    <td className={TD}>{r.broker_code}</td>
                    <td className={TD}>
                      <Badge variant={LICENCE_VARIANT[r.licence_state]} size="sm">
                        {r.licence_state}
                      </Badge>
                    </td>
                    <td className={TD}>{date(r.created_at)}</td>
                    <td className={TD}>
                      {tab === 'pending' ? (
                        excerpt(r.note)
                      ) : (
                        <>
                          <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                            {STATUS_LABEL[r.status]}
                          </Badge>{' '}
                          {date(r.decided_at)} {r.decided_by && `by ${r.decided_by}`}
                          {r.decided_by_side && ` (${SIDE_LABEL[r.decided_by_side] ?? r.decided_by_side})`}
                          {r.decline_reason && <span className="block text-xs text-gray-500">{r.decline_reason}</span>}
                        </>
                      )}
                    </td>
                    <td className={TD}>
                      <Button size="sm" variant="outline" onClick={() => setOpenId(r.id)}>
                        View {r.broker_name}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="space-y-3 md:hidden">
              {rows.map((r) => (
                <li key={r.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{r.broker_name}</span>
                    <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                      {STATUS_LABEL[r.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {r.broker_code} - {date(r.created_at)}
                  </p>
                  {r.note && <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{excerpt(r.note)}</p>}
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => setOpenId(r.id)}>
                    View {r.broker_name}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
      {openId && <RequestDetail id={openId} canDecide={canDecide} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function RequestDetail({ id, canDecide, onClose }: { id: string; canDecide: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [unticked, setUnticked] = useState<Set<string>>(new Set());

  const { data, isLoading, isError } = useQuery<BrokerRequest>({
    queryKey: ['insurerBrokerRequest', id],
    queryFn: async () => (await api.get(`${BASE}${id}/`)).data,
  });

  const decide = useMutation({
    mutationFn: async (body: { action: 'approve' | 'reject'; payload: Record<string, unknown> }) =>
      (await api.post(`${BASE}${id}/${body.action}/`, body.payload)).data,
    onSuccess: (_d, vars) => {
      toast.success(vars.action === 'approve' ? 'Broker approved' : 'Request rejected');
      queryClient.invalidateQueries({ queryKey: ['insurerBrokerRequests'] });
      queryClient.invalidateQueries({ queryKey: ['insurerBrokerRequest', id] });
      queryClient.invalidateQueries({ queryKey: ['insurerBrokerRequestsPending'] });
      onClose();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const reasonOk = reason.trim().length >= MIN_REASON;
  const products = data?.products ?? [];
  const approve = () => {
    const payload: Record<string, unknown> = {};
    if (unticked.size) payload.product_ids = products.filter((p) => !unticked.has(p.id)).map((p) => p.id);
    decide.mutate({ action: 'approve', payload });
  };

  return (
    <>
      <Modal isOpen={!rejecting} onClose={onClose} title={data ? data.broker_name : 'Broker request'} size="lg">
        {isLoading ? (
          <LoadingSpinner />
        ) : isError || !data ? (
          <p className="text-sm text-red-600 dark:text-red-400">This request could not be loaded.</p>
        ) : (
          <div className="space-y-4 text-sm text-gray-900 dark:text-gray-100">
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-gray-500 dark:text-gray-400">Code</dt>
              <dd>{data.broker_code}</dd>
              <dt className="text-gray-500 dark:text-gray-400">Licence</dt>
              <dd>
                {data.licence_number || 'Not on file'}{' '}
                <Badge variant={LICENCE_VARIANT[data.licence_state]} size="sm">
                  {data.licence_state}
                </Badge>{' '}
                {date(data.licence_valid_until)}
              </dd>
              <dt className="text-gray-500 dark:text-gray-400">Requested</dt>
              <dd>{date(data.created_at)}</dd>
              <dt className="text-gray-500 dark:text-gray-400">Status</dt>
              <dd>
                <Badge variant={STATUS_VARIANT[data.status]} size="sm">
                  {STATUS_LABEL[data.status]}
                </Badge>
              </dd>
            </dl>
            <div>
              <h4 className="font-medium">Broker&apos;s note</h4>
              <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{data.note || 'No note.'}</p>
            </div>
            {data.status === 'pending' && data.approve_blocker && (
              <p role="alert" className="rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                Cannot approve: {data.approve_blocker}
              </p>
            )}
            {data.status === 'pending' && data.relation_status && data.relation_status !== 'ACTIVE' && (
              <p className="text-gray-600 dark:text-gray-300">
                Existing connection: {data.relation_status.replace(/_/g, ' ').toLowerCase()}.
              </p>
            )}
            {data.status === 'pending' && canDecide && products.length > 0 && (
              <fieldset>
                <legend className="font-medium">Products this broker may sell</legend>
                <p className="text-xs text-gray-500 dark:text-gray-400">All are enabled unless you untick them.</p>
                {products.map((p) => (
                  <label key={p.id} className="mt-1 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!unticked.has(p.id)}
                      onChange={(e) => {
                        const next = new Set(unticked);
                        if (e.target.checked) next.delete(p.id);
                        else next.add(p.id);
                        setUnticked(next);
                      }}
                    />
                    {p.name}
                  </label>
                ))}
              </fieldset>
            )}
            {data.status !== 'pending' && (
              <p>
                {STATUS_LABEL[data.status]} on {date(data.decided_at)} by {data.decided_by || 'unknown'}
                {data.decided_by_side && ` (${SIDE_LABEL[data.decided_by_side] ?? data.decided_by_side})`}.
                {data.decline_reason && <span className="block">Reason: {data.decline_reason}</span>}
              </p>
            )}
            {!!data.status_history?.length && (
              <div>
                <h4 className="font-medium">History</h4>
                <ul className="text-gray-700 dark:text-gray-300">
                  {data.status_history.map((h, i) => (
                    <li key={i}>
                      {date(h.timestamp)} {h.status} by {h.user}
                      {h.reason ? ` - ${h.reason}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.status === 'pending' && canDecide && (
              <ModalFooter>
                <Button variant="danger" onClick={() => setRejecting(true)}>
                  Reject
                </Button>
                <Button onClick={approve} disabled={!data.can_approve} isLoading={decide.isPending}>
                  Approve
                </Button>
              </ModalFooter>
            )}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={rejecting}
        onClose={() => setRejecting(false)}
        title="Reject request"
        description="The broker sees this reason. Keep it professional."
      >
        <label htmlFor="reject-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Reason (required)
        </label>
        <textarea
          id="reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          maxLength={2000}
          className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        {!reasonOk && reason.length > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">At least {MIN_REASON} characters.</p>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setRejecting(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!reasonOk}
            isLoading={decide.isPending}
            onClick={() => decide.mutate({ action: 'reject', payload: { reason: reason.trim() } })}
          >
            Reject request
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
